import { Badge, Box, Card, Column, Table, toast } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { ProgramType, ProgramTypeLabels, ProgramTypes } from 'types';

import {
  HoldMotionProgramSetting,
  useGetHoldMotionProgramSettingsQuery,
  useUpdateHoldMotionProgramSettingMutation,
} from '../../../services/queues';
import { MainContainer } from '../../MainContainer';
import PageHeader from '../../PageHeader';

import { DisableProgramHoldMotionModal } from './DisableProgramHoldMotionModal';
import { HoldMotionTabs } from './HoldMotionTabs';
import { HoldQueueDrainCallout } from './HoldQueueDrainCallout';
import { ProgramHoldMotionToggle } from './ProgramHoldMotionToggle';

interface ProgramRow {
  programType: ProgramType;
  label: string;
  enabled: boolean;
  heldCount: number;
}

export const ProgramEnablement = () => {
  const { data, isFetching, refetch } = useGetHoldMotionProgramSettingsQuery();
  const [updateProgramSetting, { isLoading }] = useUpdateHoldMotionProgramSettingMutation();

  // Rows come from the full ProgramType list rather than the stored doc, so a program added after the
  // settings were seeded still renders (fail-closed as off) instead of silently disappearing.
  const rows: ProgramRow[] = useMemo(() => {
    const settingsByProgram = new Map<ProgramType, HoldMotionProgramSetting>(
      (data?.programSettings ?? []).map((setting: HoldMotionProgramSetting) => [setting.programType, setting]),
    );
    return ProgramTypes.map((programType) => ({
      programType,
      label: ProgramTypeLabels[programType],
      enabled: settingsByProgram.get(programType)?.enabled ?? false,
      heldCount: settingsByProgram.get(programType)?.heldCount ?? 0,
    }));
  }, [data]);

  const [programToDisable, setProgramToDisable] = useState<ProgramRow | null>(null);

  const saveProgramSetting = useCallback(
    async (programType: ProgramType, enabled: boolean) => {
      const label = ProgramTypeLabels[programType];
      try {
        const result = await updateProgramSetting({ programType, enabled }).unwrap();
        if (enabled) {
          toast.success(`Hold Motion enabled for ${label}.`);
          return;
        }
        if (result.releasedCount === 0) {
          toast.success(`Hold Motion disabled for ${label}.`);
          return;
        }
        toast.success(
          `Hold Motion disabled for ${label}. ${result.releasedCount} held project(s) queued for release. This can take a few minutes — reload the page in a couple minutes to see the effects.`,
          { duration: 20000 },
        );
      } catch (err) {
        // additionalContext.programHeldCount is only ever present on this one failure mode: the
        // disable was already persisted but releasing the held queues failed, so retrying is an
        // idempotent re-toggle.
        const programHeldCount = (err as { data?: { additionalContext?: { programHeldCount?: number } } })?.data
          ?.additionalContext?.programHeldCount;
        if (!enabled && typeof programHeldCount === 'number' && programHeldCount > 0) {
          toast.error(
            `Hold Motion was disabled for ${label}, but ${programHeldCount} held project(s) could not be released. Toggle it on and then off again to retry.`,
            { duration: 20000 },
          );
          return;
        }
        toast.error(`Failed to update Hold Motion for ${label}`);
      }
    },
    [updateProgramSetting],
  );

  // Only disabling needs confirmation — it releases held projects irreversibly, while enabling just
  // starts holding new approvals.
  const handleToggle = useCallback(
    async (programType: ProgramType, enabled: boolean) => {
      const label = ProgramTypeLabels[programType];
      if (enabled) {
        void saveProgramSetting(programType, true);
        return;
      }
      // A count cached from page load could read 0 and drop the type-to-confirm gate on a release
      // that is not actually empty, so re-read it before opening the modal.
      try {
        const fresh = await refetch().unwrap();
        const freshSetting = fresh?.programSettings?.find(
          (setting: HoldMotionProgramSetting) => setting.programType === programType,
        );
        setProgramToDisable({
          programType,
          label,
          enabled: freshSetting?.enabled ?? false,
          heldCount: freshSetting?.heldCount ?? 0,
        });
      } catch {
        toast.error(`Could not read the current held count for ${label}. Try again.`);
      }
    },
    [refetch, saveProgramSetting],
  );

  const handleCloseDisableModal = useCallback(() => {
    setProgramToDisable(null);
  }, []);

  const handleConfirmDisable = useCallback(async () => {
    if (!programToDisable) {
      return;
    }
    setProgramToDisable(null);
    await saveProgramSetting(programToDisable.programType, false);
  }, [programToDisable, saveProgramSetting]);

  const columns: Column[] = useMemo(
    () => [
      {
        heading: 'Program',
        dataKey: 'label',
        render: (_cell, row) => <Box fontWeight="bold">{(row as ProgramRow).label}</Box>,
      },
      {
        heading: 'Held Projects',
        dataKey: 'heldCount',
        align: 'center',
        render: (_cell, row) => (
          <Badge
            variant={(row as ProgramRow).heldCount > 0 ? 'warning' : 'secondary'}
            size="md"
            message={`${(row as ProgramRow).heldCount} held`}
          />
        ),
      },
      {
        heading: 'Hold Motion',
        dataKey: 'enabled',
        align: 'right',
        render: (_cell, row) => (
          <ProgramHoldMotionToggle
            programType={(row as ProgramRow).programType}
            enabled={(row as ProgramRow).enabled}
            // Every toggle locks during a mutation: releasing is asynchronous, so a second toggle
            // before the refetch settles would act on a count the server has already started draining.
            isDisabled={isLoading || isFetching}
            onToggle={handleToggle}
          />
        ),
      },
    ],
    [handleToggle, isLoading, isFetching],
  );

  return (
    <>
      <Helmet>
        <title>Hold Motion Program Enablement</title>
      </Helmet>
      <DisableProgramHoldMotionModal
        isOpen={!!programToDisable}
        programLabel={programToDisable?.label ?? ''}
        heldCount={programToDisable?.heldCount ?? 0}
        isLoading={isLoading}
        handleClose={handleCloseDisableModal}
        handleConfirm={handleConfirmDisable}
      />
      <MainContainer>
        <PageHeader title="Review Hold Queue"></PageHeader>
        <HoldMotionTabs />
        <Box gap="lg" padding="lg 0">
          <Box fontSize="xl" fontWeight="bold">
            Hold Motion by Program
          </Box>
          <Box display="block" color="grey-600">
            Toggle Hold Motion on or off by program. When a program is <strong>on</strong>, every milestone approval
            event configured to hold will start holding eligible projects for that program.
          </Box>
          <HoldQueueDrainCallout />
          <Card>
            <Table rowKey="programType" columns={columns} rows={rows} align="left" />
          </Card>
        </Box>
      </MainContainer>
    </>
  );
};
